import {
  Component,
  ViewChild,
  inject,
  computed,
  effect,
  ChangeDetectionStrategy,
  DestroyRef,
  ChangeDetectorRef,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';

import { SubscriptionDto } from '../subscription-dto';
import { OrderDto } from '../order-dto';
import { OrderConfigComponent } from '../order-list/order-config/order-config.component';
import { AccessConfigService } from '../../access-config.service';
import { ApiService } from '../../../core/api/api.service';
import { AuthenticationService } from '../../../core/authentication/authentication.service';
import { ConfirmationService } from '../../../shared/dialog/confirmation.service';
import { MessageBoxService } from '../../../shared/dialog/messagebox.service';
import { ModuleStateService } from '../../../module-state.service';
import { LoaderService } from '../../../shared/loader/loader.service';
import { MatomoTracker } from 'ngx-matomo-client';
import { extract } from '../../../core/i18n.service';
import { Logger } from '../../../core/logger.service';
import { MAIL_TYPE, SendMailDto } from '../../../core/dto/sub-config-action-dto';
import { SnackBarService } from '../../../snackbar.service';
import { MatMenuModule } from '@angular/material/menu';

const logger = new Logger('subscription-item');

@Component({
  selector: 'app-order',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatExpansionModule,
    MatInputModule,
    MatCardModule,
    MatTooltipModule,
    MatSelectModule,
    MatDividerModule,
    TranslateModule,
    OrderConfigComponent,
    MatMenuModule,
    MatIconModule,
  ],
  templateUrl: './order.component.html',
  styleUrl: './order.component.scss'
})
export class OrderComponent {
  // ---- Inputs ----
  subscription = input.required<SubscriptionDto>();
  expanded = input(false);
  compressed = input.required<boolean>();
  medium = input.required<boolean>();
  minimumState = input.required<boolean>();
  isOperator = input.required<boolean>();
  canSendMail = input.required<boolean>();
  canEditOrder = input.required<boolean>();
  canDeleteSubscription = input.required<boolean>();
  enabledDetail = input.required<boolean>();
  searchedText = input('');

  // ---- Outputs ----
  configAccessClick = output<void>();
  openStatsClick = output<void>();
  renewClick = output<void>();
  buyCreditsClick = output<void>();
  deleteClick = output<void>();
  panelOpened = output<void>();
  panelClosed = output<void>();
  orderUpdated = output<void>();

  // ---- Stato locale ----
  @ViewChild('orderForm') orderForm: OrderConfigComponent | undefined;
  editOrderForm: FormGroup;
  public oldAiCredits = -1;

  // ---- Services ----
  private translateService = inject(TranslateService);
  private accessConfigService = inject(AccessConfigService);
  private authenticationService = inject(AuthenticationService);
  private confirmationService = inject(ConfirmationService);
  private messageBoxService = inject(MessageBoxService);
  private moduleStateService = inject(ModuleStateService);
  private loaderService = inject(LoaderService);
  private apiService = inject(ApiService);
  private tracker = inject(MatomoTracker);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private snackbarService = inject(SnackBarService);

  public MailType = MAIL_TYPE;

  constructor() {
    this.editOrderForm = this.fb.group({});

    effect(() => {
      const credits = this.subscription().order.aiCredits;
      if (credits !== -1 && this.oldAiCredits === -1) {
        this.oldAiCredits = credits;
      }
    });
  }

  public markForCheck() {
    this.cdr.markForCheck();
  }

  canSendAdminMail = computed(() => {
    const sub = this.subscription();
    return this.isOperator() && sub.access?.admin?.eMail;
  });
  getLicenseTypeIcon = computed(() => {
    const sub = this.subscription();
    switch (sub.order.licenseType) {
      case 's': {
        if (sub.order.origin === 'demo' && sub.order.subscriptionId.startsWith('demo-')) {
          return 'timelapse';
        } else {
          return 'person';
        }
      }
      case 'm':
        return 'groups_2';
      case 'd':
        return 'domain';
      default:
        return 'question_mark';
    }
  });

  // ---- Formattazione ----
  getLicenseType(licenseType: string, domainLics: number): string {
    switch (licenseType) {
      case 's':
        return this.translateService.instant('ACCESSCONFIG_LICENSE_SINGLE');
      case 'm':
        return this.translateService.instant('ACCESSCONFIG_LICENSE_MULTI');
      case 'd':
        const desc =
          domainLics <= 0
            ? this.translateService.instant('ACCESSCONFIG_UNLIMITED_LICENSES')
            : this.translateService
              .instant('ACCESSCONFIG_LIMITED_LICENSES')
              .replace('%s', domainLics);
        return this.translateService.instant('ACCESSCONFIG_LICENSE_DOMAIN') + ' ' + desc;
      default:
        return 'undefined';
    }
  }

  getOriginType(origin: string) {
    return this.translateService.instant('ORIGIN_' + origin.toLocaleUpperCase());
  }

  getRefOrderId(refId: string, lastAction: string) {
    let s = refId;
    if (lastAction) s += this.translateService.instant('REFORDERID_' + lastAction.toUpperCase());
    return s;
  }

  getOrderStatus(sub: SubscriptionDto): string {
    if (!sub) return '?';
    if (sub.order.suspended) return this.translateService.instant('ACCESSCONFIG_ORDER_SUSPENDED');
    const now = new Date().toISOString().slice(0, 10);
    return now > sub.order.dbExpireDate
      ? this.translateService.instant('ACCESSCONFIG_ORDER_EXPIRED')
      : this.translateService.instant('ACCESSCONFIG_ORDER_ACTIVE');
  }

  isOrderExpired(sub: SubscriptionDto): boolean {
    if (!sub) return true;
    if (sub.order.suspended) return true;
    const now = new Date().toISOString().slice(0, 10);
    return now > sub.order.dbExpireDate;
  }


  toLocalDateTime(dbDate: string): string {
    if (!dbDate) return '';
    const d = new Date(dbDate);
    const p = (n: number, l = 2) => String(n).padStart(l, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${p(d.getFullYear(), 4)} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }

  toLocalLocked(locked: boolean): string {
    return this.translateService.instant(
      locked ? 'ACCESSCONFIG_LOCKED_TRUE' : 'ACCESSCONFIG_LOCKED_FALSE',
    );
  }

  historyExists(order: OrderDto): boolean {
    return order?.z_history ? Object.keys(order.z_history).length > 0 : false;
  }

  showHistory(order: OrderDto) {
    this.messageBoxService.showOrderHistory(order);
  }

  goToWebsiteClick(from: string, type: string) {
    this.moduleStateService.goToWebsiteClick(from, type);
    try {
      this.tracker.trackEvent('Abbonamenti', 'Click buy', 'ConvertDemoToSubscription');
    } catch (e: any) {
      logger.error('matomo error ' + e.message);
    }
  }

  // ---- Gestione form ordine (locale) ----
  showUpdateOrder(sub: SubscriptionDto) {
    if (sub.showOrder) {
      this.isDirty(sub);
    } else {
      sub.showOrder = true;
    }
  }

  isDirty(sub: SubscriptionDto) {
    const dirty = this.orderForm?.orderForm?.dirty ?? false;
    if (dirty) {
      this.confirmationService
        .confirm(
          this.translateService.instant(extract('WARNING')),
          this.translateService.instant(extract('EXIT_MESSAGE')),
          this.translateService.instant(extract('BUTTON_OK')),
          this.translateService.instant(extract('WAIT')),
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (r) => {
            if (r) {
              sub.showOrder = false;
              this.cdr.markForCheck();
            }
          },
        });
    } else {
      sub.showOrder = false;
    }
  }

  cancelOrderUpdate(sub: SubscriptionDto) {
    sub.showOrder = false;
  }

  isEnableUpdateOrder(): boolean {
    return !!(this.orderForm?.orderForm?.valid && !this.orderForm?.orderForm?.pristine);
  }

  updateOrder(sub: SubscriptionDto) {
    if (!this.orderForm?.orderForm.valid) return;
    this.loaderService.startLoading('generic', 'PLEASE_WAIT');

    const form = this.orderForm.orderForm;
    sub.order.userType = form.get('selectUserType')?.value;
    sub.order.mechCode = form.get('mechCode')?.value + '';
    sub.order.planId = form.get('planId')?.value;
    sub.order.aiCredits = form.get('aiCredits')?.value;
    sub.order.userEmail = form.get('userEmail')?.value;
    sub.order.fullName = form.get('fullName')?.value;
    sub.order.slotCount = form.get('slotCount')?.value;
    sub.order.domainLics = form.get('domainLics')?.value | 0;
    sub.order.origin = form.get('origin')?.value;
    sub.order.suspended = form.get('suspended')?.value;
    sub.order.notes = form.get('notes')?.value;
    sub.order.ocrMathScanQuota = form.get('ocrMathScanQuota')?.value;
    sub.order.ocrMathScanPolicy = form.get('ocrMathScanPolicy')?.value;

    const dateVal = form.get('startDate')?.value as Date;
    if (dateVal) {
      dateVal.setHours(12);
      sub.order.dbStartDate = dateVal.toISOString().substr(0, 10);
      const [sy, sm, sd] = sub.order.dbStartDate.split('-');
      sub.order.startDate = `${sd}/${sm}/${sy}`;
    }
    const expVal = form.get('expireDate')?.value as Date;
    if (expVal) {
      expVal.setHours(12);
      sub.order.dbExpireDate = expVal.toISOString().substr(0, 10);
      const [ey, em, ed] = sub.order.dbExpireDate.split('-');
      sub.order.expireDate = `${ed}/${em}/${ey}`;
    }

    const operator = this.authenticationService.getUserEmail();
    this.accessConfigService
      .updateOrder(
        sub.order.subscriptionId,
        sub.order.randomId,
        sub.order.userEmail,
        sub.order.fullName,
        sub.order.dbStartDate,
        sub.order.dbExpireDate,
        sub.order.origin,
        sub.order.suspended,
        sub.order.notes,
        operator,
        sub.order.slotCount,
        sub.order.domainLics,
        sub.order.mechCode,
        sub.order.planId,
        sub.order.ocrMathScanQuota,
        sub.order.ocrMathScanPolicy,
        sub.order.userType,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resp: any) => {
        const delta = sub.order.aiCredits - this.oldAiCredits;
        const finish = () => {
          this.loaderService.stopLoading('generic');
          this.messageBoxService.showTextMessage(
            this.messageBoxService.MODE_TYPE.OK,
            this.translateService.instant('ACCESSCONFIG_TITLE'),
            this.translateService.instant('ORDER_MODIFIED')
          );
          sub.order.lastModificationDate = resp.order.lastModificationDate;
          sub.order.lastModificationBy = resp.order.lastModificationBy;
          sub.order.z_history = resp.order.z_history;
          sub.showOrder = false;
          this.orderUpdated.emit();
          this.cdr.markForCheck();
        };

        if (delta !== 0) {
          this.apiService
            .buyCredits(
              this.authenticationService.getUserIdentifier(),
              sub.order.subscriptionId,
              delta,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.oldAiCredits = sub.order.aiCredits;
                finish();
              },
              error: () => {
                this.loaderService.stopLoading('generic');
                this.messageBoxService.showTextMessage(
                  this.messageBoxService.MODE_TYPE.OK,
                  this.translateService.instant('WARNING'),
                  this.translateService.instant('ACCESSCONFIG_ERROR_CREDITS'),
                );
                this.cdr.markForCheck();
              },
            });
        } else {
          finish();
        }
      });
  }

  async resendEmail(mailType: MAIL_TYPE, toAdmin: boolean) {
    let mailTo = this.subscription().order.userEmail;
    let fullName = this.subscription().order.fullName;
    if (toAdmin) {
      if (
        this.subscription().access &&
        this.subscription().access.admin &&
        this.subscription().access.admin.eMail
      ) {
        mailTo = this.subscription().access.admin.eMail;
        fullName = this.subscription().access.admin.name;
      }
    }

    try {
      await this.accessConfigService.doSubActions(
        this.subscription().order.subscriptionId,
        this.subscription().order.randomId,
        [new SendMailDto(mailTo, fullName, mailType)],
      );
    } catch (error) {
      this.snackbarService.openSnackBarSimple(
        this.translateService.instant('ERROR_MESSAGE.ACCESSCONFIG_DO_ACTIONS_FAIL'),
        this.translateService.instant('BUTTON_OK'),
        'error',
        'center',
        () => { },
        () => { },
      );
    } finally {
      this.messageBoxService.showTextMessage(
        this.messageBoxService.MODE_TYPE.OK,
        this.translateService.instant('ACCESSCONFIG_TITLE'),
        this.translateService.instant('MAIL_SENT'),
      );
    }

    /*this.accessConfigService
      .sendMail(
        this.subscription().order.subscriptionId,
        this.subscription().order.randomId,
        mailTo,
        name,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resp: any) => {
        if (resp.ok) {
          this.messageBoxService.showTextMessage(
            this.messageBoxService.MODE_TYPE.OK,
            this.translateService.instant('ACCESSCONFIG_TITLE'),
            this.translateService.instant('MAIL_SENT'),
          );
        }
      });*/
  }

  afterCollapse() {
    this.panelClosed.emit();
  }

  afterExpand() {
    this.panelOpened.emit();
  }
}
